import React from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Box, Tooltip } from '@mui/material';
import { GridActionsCellItem, type GridColDef } from '@mui/x-data-grid-premium';

import { Icon, showDeleteDialog, TabFab, useSettings, Utils } from 'app/main/components';
import { ViewTable, ViewTableUtils } from 'app/main/components/page-layouts';

import { showOrdersPromosEditDialog } from '../../components/OrdersPromosEditDialog';

export default function OrdersPromosTab() {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const [refresh, setRefresh] = React.useState(true);
	const updateRefresh = () => setRefresh(value => !value);

	function addNewPromo() {
		dispatch(showOrdersPromosEditDialog({ baseModel: {}, isAdd: true, updateRefresh }));
	}

	function editPromo(row) {
		dispatch(showOrdersPromosEditDialog({ baseModel: row, updateRefresh }));
	}

	function deletePromo(row) {
		dispatch(
			showDeleteDialog({
				module: 'orders_promos',
				id: row.orpm_uid,
				hardDelete: true,
				replace: false,
				updateRefresh
			})
		);
	}

	const columns: GridColDef[] = [
		{
			field: 'orpm_disabled',
			headerName: '',
			type: 'boolean',
			width: 36,
			renderCell: ({ value, row }) =>
				value ? (
					<Tooltip title={t('DETAILS_ORDERS_PROMOS_FIELD_DISABLED')} arrow>
						<Icon icon="block" color="error.main" />
					</Tooltip>
				) : Utils.today().startOf('day').isAfter(moment(row.orpm_d_to)) ? (
					<Tooltip title={t('DETAILS_ORDERS_PROMOS_FIELD_EXPIRED')} arrow>
						<Icon icon="close-octagon" color="error.main" />
					</Tooltip>
				) : (
					Utils.today().startOf('day').isSameOrAfter(moment(row.orpm_d_from)) && (
						<Tooltip title={t('DETAILS_ORDERS_PROMOS_FIELD_ACTIVE')} arrow>
							<Icon icon="check-bold" color="success.main" />
						</Tooltip>
					)
				),
			sortable: false
		},
		{
			field: 'orpm_title',
			headerName: t('DETAILS_ORDERS_PROMOS_FIELD_TITLE'),
			minWidth: 200,
			flex: 1,
			renderCell: ViewTableUtils.renderCellWithTooltip
		},
		{
			field: 'orpm_description',
			headerName: t('DETAILS_ORDERS_PROMOS_FIELD_DESCRIPTION'),
			minWidth: 200,
			flex: 1,
			renderCell: ViewTableUtils.renderCellWithTooltip
		},
		{
			field: 'orpm_d_from',
			headerName: t('DETAILS_ORDERS_PROMOS_FIELD_DATE_FROM'),
			minWidth: 100,
			renderCell: ViewTableUtils.renderDate
		},
		{
			field: 'orpm_d_to',
			headerName: t('DETAILS_ORDERS_PROMOS_FIELD_DATE_TO'),
			minWidth: 100,
			renderCell: ViewTableUtils.renderDate
		},
		{
			field: 'orpm_type',
			headerName: t('DETAILS_ORDERS_PROMOS_FIELD_TYPE'),
			minWidth: 200,
			renderCell: ({ value }) => ViewTableUtils.renderSquareChip({ value: t(`ORDERS_PROMOS_TYPE_${value}`) })
		},
		{
			field: 'actions',
			type: 'actions',
			headerName: t('VIEW_TABLE_COLUMN_ACTIONS'),
			width: 100,
			cellClassName: 'actions',
			getActions: ({ row }) => [
				<GridActionsCellItem
					icon={<Icon icon="edit" />}
					label={t('VIEW_ITEM_BUTTON_EDIT')}
					onClick={() => editPromo(row)}
				/>,
				<GridActionsCellItem
					icon={<Icon icon="delete" />}
					label={t('VIEW_ITEM_BUTTON_DELETE')}
					onClick={() => deletePromo(row)}
				/>
			]
		}
	];

	return (
		<Box className="w-full">
			<ViewTable module="orders_promos" columns={columns} refresh={refresh} updateRefresh={updateRefresh} />
			<TabFab label={t('ORDERS_PROMOS_ADD')} icon="add-circle" onClick={addNewPromo} />
		</Box>
	);
}
